import { ArrowLinkText } from '@/components/common/ArrowLinkText';
import { FlexCol, FlexRow } from '@/components/common/Flex';
import { LinkText, Paragraph, Text } from '@/components/common/Typography';
import IsAmountKnownBottomContent from '@/components/goals/IsAmountKnownBottomContent';
import { toDollars } from '@/util/currency';
import { ListItem, UnorderedList } from '@chakra-ui/react';
import { FV } from '@common/financeMath';
import { CarGoal } from '@common/goals';
import { getGoalBaseConfig } from '@common/react/goals';
import { CarCategory, CarPurchaseMethod, NewOrUsed } from '@common/types';
import { dt } from '@common/util/dateTime';
import { getRoundedUp } from '@common/util/math';
import {
    capitalize,
    toLowerCasePreservingAcronyms,
} from '@common/util/strings';
import { isNumber, isString } from '@common/util/types';
import isEqual from 'lodash.isequal';
import Link from 'next/link';
import GoalAssumptionsInfoLink from '../GoalAssumptionsInfoLink';
import GoalStatementLinkText from '../GoalStatementLinkText';
import {
    getFinalAssumptionsWithRisk,
    getIOrWe,
    getRecommendedRiskScoreUsingTargetDate,
    getTargetDateFromYearMonth,
    getYearsOldStr,
} from '../helpers';
import { GoalConfig, GoalData } from './types';

const purchaseMethodLabelByValue: Record<CarPurchaseMethod, string> = {
    IN_FULL: 'Buy with cash',
    LOAN: 'Buy with loan',
} as const;

const newOrUsedLabelByValue: Record<NewOrUsed, string> = {
    NEW: 'New',
    USED: 'Used',
} as const;

const categoryLabelByValue: Record<CarCategory, string> = {
    COMPACT: 'Compact',
    CROSSOVER: 'Crossover',
    SUV: 'SUV',
    PICKUP: 'Pickup',
    MINIVAN: 'Minivan',
    LUXURY_SEDAN: 'Luxury Sedan',
    LUXURY_SUV: 'Luxury SUV',
} as const;

const carInfoByCategoryByNewOrUsed: Record<
    NewOrUsed,
    Record<
        CarCategory,
        { amount: number; description: string; mostPopular: string }
    >
> = {
    NEW: {
        COMPACT: {
            amount: 26_296_00, // $26,296
            description: `Fuel efficiency and practicality lead the way.`,
            mostPopular: `Honda Civic, Toyota Corolla, Mazda 3`,
        },
        CROSSOVER: {
            amount: 35_279_00, // $35,279
            description: `For those who might need more capacity while parking in the city.`,
            mostPopular: `Toyota Rav4, Honda CR-V, Mazda Cx-5`,
        },
        SUV: {
            amount: 47_747_00, // $47,747
            description: `For the family or soon-to-be adventure-mobilers.`,
            mostPopular: `Ford Explorer, Toyota Highlander, Jeep Grand Cherokee`,
        },
        PICKUP: {
            amount: 63_456_00, // $63,456
            description: `The ultimate in rugged durability for towing or hauling.`,
            mostPopular: `Ford F-150, Chevy Silverado, Ram 1500`,
        },
        MINIVAN: {
            amount: 47_493_00, // $47,493
            description: `A family-friendly ride for your kids (and their friends!)`,
            mostPopular: `Honda Odyssey, Toyota Sienna, Chrysler Pacifica`,
        },
        LUXURY_SEDAN: {
            amount: 69_693_00, // $69,693
            description: `Performance, comfort, and leather are the name of the game.`,
            mostPopular: `BMW 5 Series, Mercedes E-Class, Audi A6`,
        },
        LUXURY_SUV: {
            amount: 69_459_00, // $69,459
            description: `Style paired with carrying capacity for a suitcase (or 6!)`,
            mostPopular: `Range Rover Sport, BMW X5, Audi Q7`,
        },
    },
    USED: {
        COMPACT: {
            amount: 22_764_00, // $22,764
            description: `Fuel efficiency and practicality lead the way.`,
            mostPopular: `Honda Civic, Toyota Corolla, Mazda 3`,
        },
        CROSSOVER: {
            amount: 26_667_00, // $26,667
            description: `For those who might need more capacity while parking in the city.`,
            mostPopular: `Toyota Rav4, Honda CR-V, Subaru Forester`,
        },
        SUV: {
            amount: 38_403_00, // $38,403
            description: `For the family or soon-to-be adventure-mobilers.`,
            mostPopular: `Toyota 4Runner, Jeep Wrangler, Chevy Tahoe`,
        },
        PICKUP: {
            amount: 37_474_00, // $37,474
            description: `The ultimate in rugged durability for towing or hauling.`,
            mostPopular: `Ford F-150, Chevy Silverado, Ram 1500`,
        },
        MINIVAN: {
            amount: 22_927_00, // $22,927
            description: `A family-friendly ride for your kids (and their friends!)`,
            mostPopular: `Honda Odyssey, Toyota Sienna, Chrysler Town & Country`,
        },
        LUXURY_SEDAN: {
            amount: 35_682_00, // $35,682
            description: `Performance, comfort, and leather are the name of the game.`,
            mostPopular: `Lexus ES, Mercedes C-Class, BMW 3 Series`,
        },
        LUXURY_SUV: {
            amount: 35_563_00, // $35,563
            description: `Style paired with carrying capacity for a suitcase (or 6!)`,
            mostPopular: `Lexus RX, Acura MDX, BMW X3`,
        },
    },
};

const getCarCategoryLabelLowerCase = (category: CarCategory) =>
    toLowerCasePreservingAcronyms(categoryLabelByValue[category]);

export const CAR: GoalConfig<CarGoal> = {
    inputSteps: [
        {
            id: 'isCarPriceKnown',
            title: 'Do you know how much you’d like to spend on your car?',
            fieldConfigs: [
                {
                    fieldName: 'isCarPriceKnown',
                    fieldType: 'radioCards',
                    fieldOptions: [
                        { value: 'true', label: 'Yes' },
                        { value: 'false', label: 'No' },
                    ],
                    fieldValueDefault: 'false',
                    bottomContent: ({ goalData }) => (
                        <IsAmountKnownBottomContent
                            blogHref="https://www.withplenty.com/blog/buying-a-car-what-you-need-to-know"
                            blogLinkText="See Plenty’s guide on buying a car"
                            isAmountKnown={goalData.isCarPriceKnown}
                        />
                    ),
                },
            ],
        },
        {
            id: 'purchaseMethod',
            title: 'How do you want to pay for your car?',
            fieldConfigs: [
                {
                    fieldName: 'purchaseMethod',
                    fieldType: 'radioCards',
                    fieldOptions: Object.entries(
                        purchaseMethodLabelByValue
                    ).map(([value, label]) => ({ value, label })),
                    fontSize: 'lg',
                    bottomContent: ({ goalData, showSlideup }) => (
                        <FlexCol mt={8} gap={3} h={18}>
                            {goalData.purchaseMethod === 'IN_FULL' ? (
                                <Paragraph fontSize="sm" color="textMuted">
                                    {`Interest rates have gone up; if you have the cash, you can save the cost of interest by not taking out a loan.`}
                                </Paragraph>
                            ) : goalData.purchaseMethod === 'LOAN' ? (
                                <Paragraph fontSize="sm" color="textMuted">
                                    {`You're ready to get started with a down payment and a monthly payment.`}
                                </Paragraph>
                            ) : undefined}
                            <FlexRow>
                                <GoalAssumptionsInfoLink
                                    onClick={() =>
                                        showSlideup('purchaseMethodInfo')
                                    }
                                    onClickEvent="Goal Assumptions Info Button Clicked"
                                    onClickEventParams={{
                                        location: 'Car Goal Guidance',
                                        card_id: 'purchaseMethod',
                                        label: 'Things to consider',
                                    }}
                                >
                                    {'Things to consider'}
                                </GoalAssumptionsInfoLink>
                            </FlexRow>
                        </FlexCol>
                    ),
                },
            ],
            onNext: ({ goalData, goForward }) => {
                if (goalData.isCarPriceKnown === true) {
                    goForward('purchaseDate');
                } else {
                    goForward();
                }
            },
        },
        {
            id: 'carKind',
            title: `Ideally, what kind of car would you get?`,
            fieldConfigs: [
                {
                    fieldName: 'newOrUsed',
                    fieldType: 'select',
                    fieldOptions: [
                        ...Object.entries(newOrUsedLabelByValue).map(
                            ([value, label]) => ({ value, label })
                        ),
                    ],
                    placeholder: 'New or used',
                },
                {
                    fieldName: 'category',
                    fieldType: 'select',
                    fieldOptions: [
                        ...Object.entries(categoryLabelByValue).map(
                            ([value, label]) => ({ value, label })
                        ),
                    ],
                    placeholder: 'Car type',
                },
            ],
            fieldContainerFlexProps: { direction: 'row' },
            bottomContent: ({ goalData }) => {
                if (!goalData.newOrUsed || !goalData.category)
                    return <FlexRow h={18} />;

                const { description, mostPopular } =
                    carInfoByCategoryByNewOrUsed[goalData.newOrUsed][
                        goalData.category
                    ];
                return (
                    <FlexCol h={18}>
                        <Paragraph fontSize="sm" color="textMuted" mt={4}>
                            {description}
                        </Paragraph>
                        <Paragraph fontSize="sm" color="textMuted" mt={4}>
                            {`Most popular: ${mostPopular}`}
                        </Paragraph>
                    </FlexCol>
                );
            },
        },
        {
            id: 'purchaseDate',
            title: 'Roughly, when do you think you want to buy your car?',
            fieldConfigs: [
                {
                    fieldName: 'yearMonth',
                    fieldType: 'yearMonth',
                    bottomContent: ({ goalData, userInfo }) => (
                        <Paragraph fontSize="sm" color="textMuted" mt={4} h={5}>
                            {goalData.targetDate
                                ? getYearsOldStr({
                                      date: goalData.targetDate,
                                      userInfo,
                                  })
                                : undefined}
                        </Paragraph>
                    ),
                },
            ],
            onBack: ({ goalData, goBack }) => {
                if (goalData.isCarPriceKnown === true) {
                    goBack('purchaseMethod');
                } else {
                    goBack();
                }
            },
        },
        {
            id: 'price',
            title: 'Choose a target car price that you feel comfortable with.',
            fieldConfigs: [
                {
                    fieldName: 'carPrice',
                    fieldType: 'currency',
                    min: 0,
                    slider: ({ goalData }) => {
                        return goalData.projectedCarPrice
                            ? {
                                  min: 0,
                                  max: goalData.projectedCarPrice * 2,
                                  step: 1_000_00, // $1,000
                              }
                            : {
                                  min: 0,
                                  max: 100_000_00, // $100,000
                                  step: 1_000_00, // $1,000
                              };
                    },
                    bottomContent: ({ goalData, showSlideup }) =>
                        !goalData.isCarPriceKnown &&
                        goalData.newOrUsed &&
                        goalData.category &&
                        goalData.projectedCarPrice ? (
                            <>
                                <Paragraph
                                    fontSize="sm"
                                    color="textMuted"
                                    mt={4}
                                >
                                    {`A ${newOrUsedLabelByValue[
                                        goalData.newOrUsed
                                    ].toLowerCase()} ${getCarCategoryLabelLowerCase(
                                        goalData.category
                                    )} has an average price of ${toDollars(
                                        goalData.projectedCarPrice
                                    )}. Courtesy of KBB and CarGurus.`}
                                </Paragraph>
                                <LinkText
                                    fontSize="sm"
                                    fontWeight="medium"
                                    mt={3}
                                    onClick={() => {
                                        showSlideup('priceAssumptions');
                                    }}
                                >
                                    See assumptions
                                </LinkText>
                            </>
                        ) : undefined,
                },
            ],
            onNext: ({ goalData, goForward }) => {
                // Skip down payment step if paying in full
                if (goalData.purchaseMethod === 'IN_FULL') {
                    goForward('confirmation');
                } else {
                    goForward();
                }
            },
        },
        {
            id: 'downPayment',
            title: ({ goalData }) =>
                `How much do you want to pay for the down payment on your ${
                    goalData.newOrUsed && goalData.category
                        ? `${newOrUsedLabelByValue[
                              goalData.newOrUsed
                          ].toLowerCase()} ${getCarCategoryLabelLowerCase(
                              goalData.category
                          )}`
                        : `car`
                }?`,
            fieldConfigs: [
                {
                    fieldName: 'downPaymentFraction',
                    fieldType: 'percent',
                    fieldValueDefault: 0.15,
                    slider: {
                        min: 0,
                        max: 1, // 100%
                        step: 0.01, // 1%
                    },
                    bottomContent: ({ goalData, showSlideup }) =>
                        goalData.targetAmount ? (
                            <>
                                <Text fontSize="lg" color="textMuted" mt={4}>
                                    {toDollars(goalData.targetAmount)}
                                </Text>
                                <LinkText
                                    fontSize="sm"
                                    fontWeight="medium"
                                    mt={3}
                                    onClick={() => {
                                        showSlideup('downPaymentAssumptions');
                                    }}
                                >
                                    See assumptions
                                </LinkText>
                            </>
                        ) : undefined,
                },
            ],
        },
    ],
    assumptions: [
        {
            id: 'purchaseMethodInfo',
            title: 'Things to consider',
            content: () => (
                <>
                    <Paragraph fontSize="sm" lineHeight="tall" mb={1}>
                        Loans can make a car affordable earlier. Depending on
                        your credit score, you’ll pay an interest expense in
                        return for the loan. If you already have the cash and
                        you’re not investing it, it could make sense to buy the
                        car and save the interest expense. If you have an
                        opportunity to invest that has a good return, that could
                        make an interest expense worth paying (especially if
                        it’s lower because you’ve worked towards a good credit
                        score).
                    </Paragraph>
                </>
            ),
        },
        {
            id: 'priceAssumptions',
            content: () => (
                <>
                    <Paragraph fontSize="sm" lineHeight="tall" mb={1}>
                        Your estimated car price is calculated using:
                    </Paragraph>
                    <UnorderedList fontSize="sm" lineHeight="tall" spacing={1}>
                        <ListItem>
                            Current average car prices, courtesy of KBB and
                            CarGurus
                        </ListItem>
                        <ListItem>
                            Inflation for rising car prices using U.S. Bureau of
                            Labor Statistics data (2.49% from 1935 to 2023)
                        </ListItem>
                        <ListItem>Timing of when you’d like to buy</ListItem>
                    </UnorderedList>
                </>
            ),
        },
        {
            id: 'downPaymentAssumptions',
            content: () => (
                <>
                    <Paragraph fontSize="sm" lineHeight="tall" mb={4}>
                        According to Nerd Wallet, the average down payment is
                        ~20% for a new car and ~10% for a used car.
                    </Paragraph>
                    <Link
                        href="https://www.nerdwallet.com/article/loans/auto-loans/how-much-down-payment-make-buying-car"
                        target="_blank"
                    >
                        <ArrowLinkText as="a" fontSize="sm" withUnderline>
                            Learn more here
                        </ArrowLinkText>
                    </Link>
                </>
            ),
        },
    ],
    onConfirmationBack: ({ goalData, goBack }) => {
        if (goalData.purchaseMethod === 'IN_FULL') {
            goBack('price');
        } else {
            goBack();
        }
    },
    getGoalStatementContent: ({ goalData, onGoalStatementClick }) => {
        if (
            !isNumber(goalData.targetAmount) ||
            !isString(goalData.targetDate)
        ) {
            console.warn(
                'Failed to construct goal statement due to incomplete goalData.'
            );
            console.warn(goalData);
            return null;
        }

        const iOrWe = getIOrWe(goalData);
        const color = CAR.color;

        return (
            <>
                {`${capitalize(iOrWe)} want to have `}
                <GoalStatementLinkText
                    text={toDollars(goalData.targetAmount)}
                    onClick={() =>
                        onGoalStatementClick(
                            goalData.purchaseMethod === 'IN_FULL'
                                ? 'carPrice'
                                : 'downPaymentFraction'
                        )
                    }
                    color={color}
                />
                {' for '}
                {goalData.purchaseMethod === 'LOAN' &&
                    goalData.downPaymentFraction && (
                        <>
                            <GoalStatementLinkText
                                text={`${
                                    Math.floor(
                                        goalData.downPaymentFraction * 10000
                                    ) / 100
                                }%`}
                                onClick={() =>
                                    onGoalStatementClick('downPaymentFraction')
                                }
                                color={color}
                            />
                            {' down on '}
                        </>
                    )}
                {'a '}
                {goalData.newOrUsed && (
                    <GoalStatementLinkText
                        text={`${newOrUsedLabelByValue[
                            goalData.newOrUsed
                        ].toLowerCase()} `}
                        onClick={() => onGoalStatementClick('newOrUsed')}
                        color={color}
                    />
                )}
                {goalData.category ? (
                    <>
                        <GoalStatementLinkText
                            text={getCarCategoryLabelLowerCase(
                                goalData.category
                            )}
                            onClick={() => onGoalStatementClick('newOrUsed')}
                            color={color}
                        />{' '}
                    </>
                ) : (
                    'car '
                )}
                {'by '}
                <GoalStatementLinkText
                    text={dt(goalData.targetDate).format('MMMM, YYYY')}
                    onClick={() => onGoalStatementClick('yearMonth')}
                    color={color}
                />
                {'.'}
            </>
        );
    },
    getAssumptionsContent: ({ goalData, userInfo, currentRateInfo }) =>
        getFinalAssumptionsWithRisk({
            goalData,
            recommendedRiskScore: CAR.getRecommendedRiskScore({
                goalData,
                userInfo,
            }),
            currentRateInfo,
        }),
    calculateDerivedGoalData: async ({
        oldGoalData,
        newGoalData,
    }): Promise<GoalData<CarGoal>> => {
        const targetDate =
            oldGoalData.yearMonth !== newGoalData.yearMonth &&
            newGoalData.yearMonth?.length === 6
                ? getTargetDateFromYearMonth(newGoalData.yearMonth)
                : oldGoalData.targetDate;

        const projectedCarPrice = getProjectedCarPrice({
            targetDate,
            newOrUsed: newGoalData.newOrUsed,
            category: newGoalData.category,
        });

        const isProjectedCarPriceChanged = !isEqual(
            oldGoalData.projectedCarPrice,
            projectedCarPrice
        );

        // Update carPrice if projectedCarPrice changed
        const carPrice = isProjectedCarPriceChanged
            ? projectedCarPrice
            : newGoalData.carPrice;

        const targetAmount =
            newGoalData.purchaseMethod === 'IN_FULL' && carPrice
                ? carPrice
                : newGoalData.purchaseMethod === 'LOAN' &&
                    carPrice &&
                    newGoalData.downPaymentFraction
                  ? getRoundedUp(
                        carPrice * newGoalData.downPaymentFraction,
                        250_00
                    )
                  : undefined;

        return {
            ...newGoalData,
            targetDate,
            projectedCarPrice,
            carPrice,
            targetAmount,
        };
    },
    getRecommendedRiskScore: (config) =>
        getRecommendedRiskScoreUsingTargetDate(config),
    getTargetDate: ({ goalData }) => goalData.targetDate,
    ...getGoalBaseConfig('CAR'),
};

const CAR_PRICE_INCREASE_RATE = 0.0249; // 2.49%

const getProjectedCarPrice = ({
    targetDate,
    newOrUsed,
    category,
}: {
    targetDate?: string | null;
    newOrUsed?: NewOrUsed;
    category?: CarCategory;
}): number | undefined => {
    if (!targetDate || !newOrUsed || !category) return undefined;

    const currentCarPrice =
        carInfoByCategoryByNewOrUsed[newOrUsed][category].amount;

    const floatYearsUntilTargetGoal = Math.max(
        dt(targetDate).diff(dt(), 'years', true),
        0 // avoid negative numbers
    );

    const projectedAmountFloat = FV({
        startingBalance: currentCarPrice,
        interestRate: CAR_PRICE_INCREASE_RATE,
        numPeriods: floatYearsUntilTargetGoal,
    });

    // Round up to nearest $1,000
    const projectedAmount = getRoundedUp(projectedAmountFloat, 1_000_00);

    return projectedAmount;
};
